// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

/**
 * Delivering the artefact: save to disk (File System Access with an anchor
 * fallback), copy to clipboard, and Web Share. Every path is guarded — a
 * browser without an API simply doesn't offer that button.
 */

interface SaveOptions {
  suggestedName: string;
  mime: string;
  description: string;
  ext: string;
}

/** Save a blob, preferring the File System Access picker. Returns false if cancelled. */
export async function saveBlob(blob: Blob, opts: SaveOptions): Promise<boolean> {
  const anyWin = window as unknown as {
    showSaveFilePicker?: (o: unknown) => Promise<{
      createWritable: () => Promise<{ write: (b: Blob) => Promise<void>; close: () => Promise<void> }>;
    }>;
  };
  if (typeof anyWin.showSaveFilePicker === 'function') {
    try {
      const handle = await anyWin.showSaveFilePicker({
        suggestedName: opts.suggestedName,
        types: [{ description: opts.description, accept: { [opts.mime]: [`.${opts.ext}`] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return true;
    } catch (err) {
      if ((err as DOMException)?.name === 'AbortError') return false;
      // Fall through to the anchor download on any other failure.
    }
  }
  anchorDownload(blob, opts.suggestedName);
  return true;
}

export function anchorDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function canCopyImage(): boolean {
  return typeof ClipboardItem !== 'undefined' && !!navigator.clipboard?.write;
}

export async function copyImage(blob: Blob): Promise<boolean> {
  if (!canCopyImage()) return false;
  try {
    await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
    return true;
  } catch {
    return false;
  }
}

export function canShareFiles(files: File[]): boolean {
  const nav = navigator as unknown as { canShare?: (d: { files: File[] }) => boolean; share?: unknown };
  return typeof nav.share === 'function' && typeof nav.canShare === 'function' && nav.canShare({ files });
}

export async function shareFiles(files: File[], title: string, text: string): Promise<boolean> {
  const nav = navigator as unknown as { share?: (d: unknown) => Promise<void> };
  if (typeof nav.share !== 'function') return false;
  try {
    await nav.share({ files, title, text });
    return true;
  } catch (err) {
    if ((err as DOMException)?.name === 'AbortError') return false;
    return false;
  }
}

/** Turn a canvas into a PNG blob. */
export function canvasToPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not render the image.'))), 'image/png');
  });
}
